import React, { useRef, useEffect, useState } from 'react';
import { find } from 'lodash';
import moment from 'moment-timezone';

import NoContent from '../no_content';
import AnnotationPopover from './annotation_popover';
import { Annotation, EditedResponseFilter } from '../../types';

import MagooshAI from '../assets/magoosh_ai.svg';
import style from './style.module.scss'

interface Props {
  activeFilters: EditedResponseFilter[];
  editedResponse: {
    content: string;
    diff: string;
    score: number;
  };
  examName: string;
  filters: EditedResponseFilter[];
  gradedAt: number;
  onChangeFilters: (filters: EditedResponseFilter[]) => void;
  score: number;
  status: 'waiting' | 'processing' | 'finished' | 'error';
}

export default (props: Props) => {
  if (props.status === 'finished' && props.examName === 'GRE' && props.score >= 5) {
    return <NoContent message="You already have a great score and we weren't able to generate suggested edits this time." />;
  } else if (!props.editedResponse && props.status === 'finished') {
    return <NoContent message="We weren't able to generate suggested edits this time." />;
  } else if (!props.editedResponse) {
    return <NoContent />;
  }

  const diffRef = useRef<HTMLDivElement>(null);
  const [activeAnnotation, setActiveAnnotation] = useState<Annotation>(null);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const filteredEssayDiff = () => {
    let diff = props.editedResponse.diff;

    props.activeFilters.forEach((filter) => {
      const regex = new RegExp(`class=\"${filter.value}\"`, 'g');
      diff = diff.replace(regex, `class="${filter.value} active"`);
    });

    return diff;
  };

  // Some <ins> elements in the diff HTML will have a data-annotation and data-annotation-index,
  // which indicates there is addition context we can display in an overlay. Number the
  // annotations in the UI and handle a click to display the annotation details.
  useEffect(() => {
    if (diffRef.current) {
      const elementsWithAnnotations = diffRef.current.querySelectorAll('.active[data-annotation-index]');

      elementsWithAnnotations.forEach((element: HTMLElement) => {
        const annotationIndex = element.getAttribute('data-annotation-index');

        if (annotationIndex && !element.nextElementSibling?.classList.contains('annotation-index')) {
          const span = document.createElement('span');
          span.className = 'annotation-index';
          span.textContent = annotationIndex;
          element.insertAdjacentElement('afterend', span);
          element.style.cursor = 'pointer';

          // On click, set the active, visible annotation details
          element.addEventListener('click', (e) => {
            e.preventDefault();
            setActiveAnnotationFromElement(element);
          });
        }
      });
    }
  }, [filteredEssayDiff()]);

  const clearActiveAnnotationClasses = () => {
    document.querySelectorAll('.active-annotation').forEach((element) => (
      element.classList.remove('active-annotation')
    ));
  };

  const handleNextAnnotation = () => {
    if (!activeAnnotation || !diffRef.current) return;

    // Find the next element with active class and data-annotation-index following the current one
    let nextElement = activeAnnotation.element.nextElementSibling;

    while (nextElement) {
      if (nextElement.classList.contains('active') && nextElement.hasAttribute('data-annotation-index')) {
        setActiveAnnotationFromElement(nextElement);
        return;
      }
      nextElement = nextElement.nextElementSibling;
    }

    // If no next element found, wrap around to first annotation
    makeFirstAnnotationActive();
  };

  const hasAnnotations = () => {
    return props.editedResponse.diff.indexOf('data-annotation') !== -1;
  };

  const makeFirstAnnotationActive = () => {
    props.onChangeFilters(props.filters);

    // Small timeout needed so that the content of the newly active filters get back in the props
    setTimeout(() => {
      const firstElement = diffRef.current.querySelector('.active[data-annotation-index]') as HTMLElement;
      if (firstElement) {
        setActiveAnnotationFromElement(firstElement);
      }
    }, 250);
  };

  const setActiveAnnotationFromElement = (element) => {
    clearActiveAnnotationClasses();
    element.classList.add('active-annotation');

    const category = find(
      props.filters,
      (filter) => element.classList.contains(filter.value)
    ) as EditedResponseFilter;
    setActiveAnnotation({
      category: category?.name,
      content: element.getAttribute('data-annotation'),
      element,
      index: parseInt(element.getAttribute('data-annotation-index'))
    });
  };

  return (
    <>
      {
        hasAnnotations() && (
          <>
            <div className="align-items-md-center d-flex flex-column flex-md-row gap-s">
              <div className="d-flex flex-none">
                <div
                  className="align-items-center d-flex flex-none gap-xxs"
                  onClick={makeFirstAnnotationActive}
                  style={{
                    background: 'linear-gradient(90deg, #0577E9, #5227F8)',
                    borderRadius: 24,
                    boxShadow: '0px 2px 2px -1px #0000000D, 0px 5px 5px -2.5px #0000000D, 0px 0px 0px 1px #0000001A',
                    color: '#fff',
                    cursor: 'pointer',
                    padding: '10px 20px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none"><path fill="#fff" d="M10.313 5.813a.563.563 0 0 0-1.126 0c0 1.819-.401 3-1.137 3.737-.737.736-1.918 1.137-3.738 1.137a.563.563 0 0 0 0 1.126c1.82 0 3.001.401 3.738 1.137.736.737 1.137 1.918 1.137 3.738a.563.563 0 0 0 1.126 0c0-1.82.401-3.001 1.137-3.737.737-.737 1.918-1.139 3.738-1.139a.563.563 0 0 0 0-1.124c-1.82 0-3.002-.402-3.738-1.138-.736-.737-1.137-1.918-1.137-3.738ZM4.5 4.125a.375.375 0 0 0-.75 0c0 .735-.163 1.18-.43 1.446-.266.266-.71.429-1.445.429a.375.375 0 0 0 0 .75c.735 0 1.18.163 1.446.43.266.266.429.71.429 1.445a.375.375 0 0 0 .75 0c0-.735.163-1.18.43-1.446.266-.266.71-.429 1.445-.429a.375.375 0 0 0 0-.75c-.735 0-1.18-.163-1.446-.43-.266-.266-.429-.71-.429-1.445ZM8.25 1.125a.375.375 0 0 0-.75 0c0 .475-.106.732-.25.875-.143.144-.4.25-.875.25a.375.375 0 1 0 0 .75c.475 0 .732.106.875.25.144.143.25.4.25.875a.375.375 0 0 0 .75 0c0-.475.106-.732.25-.875.143-.144.4-.25.875-.25a.375.375 0 0 0 0-.75c-.475 0-.732-.106-.875-.25-.144-.143-.25-.4-.25-.875Z"/></svg>
                  <div><strong>Start Review</strong></div>
                </div>
              </div>

              <strong>We’ll walk you through each edit and why it was made.</strong>
            </div>

            <hr className="u-margin-A-xxs" />
          </>
        )
      }

      <div className="align-items-center d-flex gap-s">
        <img alt="" src={MagooshAI} />

        <div className="small">
          Magoosh AI Tutor
          {' '} on {moment.unix(props.gradedAt).tz(timeZone).format('MMMM D, YYYY')}
        </div>
      </div>

      <div className="d-flex flex-column" style={{ gap: 20 }}>
        <div
          className={style.editedEssay}
          dangerouslySetInnerHTML={{ __html: filteredEssayDiff() }}
          ref={diffRef}
        />
      </div>

      {
        activeAnnotation && (
          <AnnotationPopover
            content={activeAnnotation.content}
            category={activeAnnotation.category}
            element={activeAnnotation.element}
            index={activeAnnotation.index}
            onClose={() => {
              clearActiveAnnotationClasses();
              setActiveAnnotation(null);
            }}
            onNext={handleNextAnnotation}
          />
        )
      }
    </>
  );
};
